// client/src/components/marketer/MarketerProjectOnCheck.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  readOngoingProjectList,
  cacheOngoingProjectList
} from '../../utils/marketerOngoingCache';
import ProjectDetailModal from './project/modals/ProjectDetailModal';

// ✅ 이미지 프리로드 함수 (브라우저 캐시 활용)
const preloadImage = (url) => {
  if (!url) return;
  const img = new Image();
  img.src = url;
};

// ✅ 날짜의 23:59:59를 반환 (한국시간)
const getEndOfDayKST = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  date.setHours(23, 59, 59, 999);
  return date;
};

// ✅ 결과물 상태 라벨
const getResultStatusLabel = (status) => {
  const labels = {
    'none': { text: '미제출', color: 'var(--mb-text-sub)' },
    'submitted': { text: '검토 대기', color: 'var(--mb-color-warning)' },
    'approved': { text: '승인', color: 'var(--mb-color-success)' },
    'rejected': { text: '거절', color: 'var(--mb-color-danger)' },
    'expired': { text: '기한 만료', color: 'var(--mb-color-danger)' },
    'completed': { text: '완료', color: 'var(--mb-color-success)' },
    'admin_reviewing': { text: '관리자 검토중', color: 'var(--mb-color-info)' },
    'admin_approved': { text: '최종 승인', color: 'var(--mb-color-success)' },
    'admin_rejected': { text: '계약 취소', color: 'var(--mb-color-danger)' }
  };
  return labels[status] || { text: '-', color: 'var(--mb-text-sub)' };
};

// ✅ 결과물 상태 배지 클래스 (렌더 전용: 기존 색 분기 → mb-badge 매핑)
const getResultBadgeClass = (status) => {
  const map = {
    'none': 'mb-badge-gray',
    'submitted': 'mb-badge-amber',
    'approved': 'mb-badge-green',
    'rejected': 'mb-badge-red',
    'expired': 'mb-badge-red',
    'completed': 'mb-badge-green',
    'admin_reviewing': 'mb-badge-blue',
    'admin_approved': 'mb-badge-green',
    'admin_rejected': 'mb-badge-red'
  };
  return map[status] || 'mb-badge-gray';
};

// ✅ SNS 플랫폼 아이콘 (SVG)
const getSNSIcon = (platform) => {
  const iconMap = {
    instagram: '/images/sns/instagram.svg',
    youtube: '/images/sns/youtube.svg',
    blog: '/images/sns/blog.svg',
    tiktok: '/images/sns/tiktok.svg'
  };

  const iconPath = iconMap[platform] || '/images/sns/default.svg';

  return (
    <img
      src={iconPath}
      alt={platform}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
};

// ✅ 팔로워 수 포맷팅 함수
const formatFollowerCount = (count) => {
  if (!count || count === 0) return '';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return String(count);
};

// ✅ 프로젝트 상태 판단 함수
const getProjectPhase = (project) => {
  if (!project) return null;

  const now = new Date();
  const recruitEnd = getEndOfDayKST(project.recruitEnd);
  const submitEnd = getEndOfDayKST(project.submitEnd);

  // 결과물 승인 마감일 = submitEnd + 7일
  const approvalEnd = submitEnd ? new Date(submitEnd.getTime() + 7 * 24 * 60 * 60 * 1000) : null;

  // 1단계: 모집 및 선정 기간 (생성일 ~ recruitEnd 23:59)
  if (recruitEnd && now <= recruitEnd) {
    return {
      phase: 1,
      message: '모집 및 선정 기간 중인 프로젝트입니다. 지원한 인플루언서를 확인하고, 원하는 인플루언서와 계약을 진행하세요.',
      buttonText: '인플루언서 선정 관리',
      buttonColor: 'var(--mb-color-primary)',
      targetSub: 'prjSelect'
    };
  }

  // 2단계: 결과물 제출 대기 및 승인 거절 처리 (recruitEnd 다음날 ~ approvalEnd 23:59)
  if (approvalEnd && now <= approvalEnd) {
    return {
      phase: 2,
      message: '결과물 제출 대기 및 승인 거절 처리 기간입니다. 인플루언서가 제출한 홍보 결과물을 확인하고 관리해보세요.',
      buttonText: '결과물 승인, 거절하기',
      buttonColor: 'var(--mb-color-warning)',
      targetSub: 'prjResult'
    };
  }

  // 3단계: 구매당 리워드 결산 기간
  if (project.hasPerPurchaseReward && project.settlementDates?.length > 0) {
    const lastSettlementDate = new Date(project.settlementDates[project.settlementDates.length - 1]);
    const cpsEnd = new Date(lastSettlementDate);
    cpsEnd.setHours(23, 59, 59, 999);
    cpsEnd.setDate(cpsEnd.getDate() + 7); // 마지막 결산일 + 7일

    if (now <= cpsEnd) {
      return {
        phase: 3,
        message: '구매당 리워드 결산 기간 중입니다. 결산을 위해 구매당 리워드 결산하기 화면에서 일정을 확인하고 결산을 진행해주세요.',
        buttonText: '구매당 리워드 결산',
        buttonColor: 'var(--mb-color-secondary)',
        targetSub: 'prjCpsCharge'
      };
    }
  }

  // 4단계: 완료 처리 대기
  return {
    phase: 4,
    message: '결과물 확인 및 결산이 완료되어, 셀프애드팀의 완료처리 대기중인 프로젝트 입니다. 완료 처리 시, 달성되지 못한 성과에 대해서는 환불처리되며, 진행된 결과물과 수치를 확인할 수 있습니다.',
    buttonText: null,
    buttonColor: null,
    targetSub: null
  };
};

// 렌더 전용: 단계 라벨 / 배지 클래스
const getPhaseLabel = (phase) => {
  if (phase === 1) return '모집 및 선정';
  if (phase === 2) return '결과물 확인';
  if (phase === 3) return 'CPS 결산';
  if (phase === 4) return '완료 처리 중';
  return '';
};

const getPhaseBadgeClass = (phase) => {
  if (phase === 1) return 'mb-badge-blue';
  if (phase === 2) return 'mb-badge-green';
  if (phase === 3) return 'mb-badge-amber';
  return 'mb-badge-gray';
};

function MarketerProjectOnCheck() {
  const navigate = useNavigate();
  const [uid, setUid] = useState(window.__ME__?.id);

  useEffect(() => {
    const currentUid = window.__ME__?.id;
    if (currentUid !== uid) {
      setUid(currentUid);
    }

    const handleUserReady = (event) => {
      const userId = event.detail?.id;
      if (userId) {
        setUid(userId);
      }
    };

    if (!window.__USER_READY_LISTENERS__) {
      window.__USER_READY_LISTENERS__ = [];
    }
    window.__USER_READY_LISTENERS__.push(handleUserReady);

    return () => {
      if (window.__USER_READY_LISTENERS__) {
        const idx = window.__USER_READY_LISTENERS__.indexOf(handleUserReady);
        if (idx > -1) {
          window.__USER_READY_LISTENERS__.splice(idx, 1);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialProjects = useMemo(() => {
    return uid ? (readOngoingProjectList(uid) || []) : [];
  }, [uid]);

  const hasProjectCache = uid && initialProjects.length > 0;

  const [projects, setProjects] = useState(initialProjects);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [loading, setLoading] = useState(!hasProjectCache);
  const [error, setError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!hasProjectCache);
  const [refreshing, setRefreshing] = useState(false);

  const fetchProjects = useCallback(async (force = false) => {
    if (!uid) return;

    try {
      setLoading(true);
      setError(null);

      const res = await fetch('/api/marketer/projects/ongoing', {
        credentials: 'include'
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.message || '프로젝트 목록 조회 실패');
      }

      setProjects(data.projects || []);

      cacheOngoingProjectList(uid, data.projects || []);

      if (data.projects && data.projects.length > 0) {
        data.projects.slice(0, 3).forEach(p => {
          if (p.cover) preloadImage(p.cover);
        });
      }

    } catch (err) {
      console.error('프로젝트 목록 조회 에러:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) return;

    if (hasProjectCache) {
      setIsInitialLoad(false);
      fetchProjects();
    } else {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const currentProject = projects[currentProjectIndex];

  const currentPhase = useMemo(() => {
    return currentProject ? getProjectPhase(currentProject) : null;
  }, [currentProject]);

  // ✅ 새로고침 함수
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchProjects(true); // force=true
    } catch (e) {
      console.error('새로고침 오류:', e);
    } finally {
      setRefreshing(false);
    }
  }, [fetchProjects]);

  const handleNavigateToPhase = useCallback((targetSub, projectId) => {
    navigate(`/marketer/mybiz?tab=project&sub=${targetSub}&projectId=${projectId}`);
  }, [navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  if (isInitialLoad && loading) {
    return (
      <div className="mb-card" style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 15, color: '#6b7686' }}>프로젝트 목록을 불러오는 중...</div>
      </div>
    );
  }

  if (error && !currentProject) {
    return (
      <div className="mb-card" style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ color: '#e5484d', fontWeight: 700, marginBottom: 16 }}>{error}</div>
        <button onClick={fetchProjects} className="mb-btn mb-btn-secondary">
          다시 시도
        </button>
      </div>
    );
  }

  if (!loading && projects.length === 0) {
    return (
      <div className="mb-card" style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 15, color: '#3b4658', fontWeight: 600 }}>진행중인 프로젝트가 없습니다.</div>
      </div>
    );
  }

  return (
    <div>
      {/* 상단 안내 */}
      <div className="mb-page-header">
        <h2 className="mb-page-title">진행중인 프로젝트 보기</h2>
        <p className="mb-page-description">진행 중인 프로젝트의 상태를 확인하고 관리할 수 있습니다.</p>
      </div>

      {/* A안: 프로젝트 카드 리스트 (‹› 페이저 대체 — 카드를 누르면 아래 상세가 바뀝니다) */}
      {projects.length > 1 && (
        <div className="mbh-plist" style={{ marginBottom: 16 }}>
          {projects.map((p, i) => {
            const ph = getProjectPhase(p);
            return (
              <button
                key={p.id}
                type="button"
                className="mbh-pcard"
                style={i === currentProjectIndex ? { boxShadow: '0 0 0 2px var(--mb-color-primary), var(--mb-shadow-card)' } : undefined}
                onClick={() => setCurrentProjectIndex(i)}
              >
                <span className="mbh-pcard__logo" aria-hidden="true">
                  {p.cover ? <img src={p.cover} alt="" /> : (p.productName || '프').charAt(0)}
                </span>
                <span className="mbh-pcard__meta">
                  <span className="mbh-pcard__name">{p.productName || '광고 대상'}</span>
                  <span className="mbh-pcard__sub">
                    NO.{p.projectNumber || 'N/A'} · 계약 {(p.contractedInfluencers || []).length}/{p.headcount || 0}명
                  </span>
                </span>
                <span className="mbh-pcard__pipe">
                  <span className="mbh-pipe">
                    {[1, 2, 3, 4].map(n => (
                      <i key={n} className={n < ph.phase ? 'is-done' : n === ph.phase ? 'is-now' : ''} />
                    ))}
                  </span>
                  <span className="mbh-pcard__stage">{ph.phase}단계 · {getPhaseLabel(ph.phase)}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {currentProject && (
        <>
          {/* 프로젝트 상세 카드 */}
          <div className="pnav">
            <div className="pnav__top">
              <div className="pnav__spacer" />
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                className="mb-btn mb-btn-secondary mb-btn-sm"
              >
                {refreshing ? '불러오는 중...' : '새로고침'}
              </button>
              <button
                type="button"
                onClick={() => setShowDetailModal(true)}
                className="mb-btn mb-btn-secondary mb-btn-sm"
              >
                프로젝트 상세보기
              </button>
            </div>

            <div className="pnav__no">PROJECT NO.{currentProject.projectNumber || 'N/A'}</div>
            <div className="pnav__title">
              {currentProject.productName || '광고 대상'}을(를) 홍보하는{' '}
              {currentProject.serviceType === 'delivery' && '제품 제공'}
              {currentProject.serviceType === 'purchase' && '제품 미제공'}
              {currentProject.serviceType === 'visit' && '장소 방문'}
              {currentProject.serviceType === 'free' && '자율'}
              {!currentProject.serviceType && '광고'}
              {' '}프로젝트입니다
            </div>

            {/* 현재 단계 박스 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 14,
              flexWrap: 'wrap',
              border: '1.5px solid #eceef2',
              borderRadius: 14,
              padding: '14px 16px'
            }}>
              <div style={{ minWidth: 0 }}>
                <span className={`mb-badge ${getPhaseBadgeClass(currentPhase?.phase)}`}>
                  {currentPhase?.phase}단계 · {getPhaseLabel(currentPhase?.phase)}
                </span>
                <div className="t-sub" style={{ marginTop: 6 }}>
                  계약 {(currentProject.contractedInfluencers || []).length}/{currentProject.headcount || 0}명
                  {' · '}생성일 {formatDate(currentProject.createdAt)}
                </div>
                {currentPhase?.message && (
                  <div className="t-sub" style={{ marginTop: 4, lineHeight: 1.5 }}>
                    {currentPhase.message}
                  </div>
                )}
              </div>

              {currentPhase?.buttonText ? (
                <button
                  type="button"
                  onClick={() => handleNavigateToPhase(currentPhase.targetSub, currentProject.id)}
                  className="mb-btn mb-btn-primary"
                  style={{ flexShrink: 0 }}
                >
                  {currentPhase.buttonText} →
                </button>
              ) : (
                <div style={{
                  padding: '10px 14px',
                  background: '#f6f8fb',
                  borderRadius: 12,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: '#6b7686',
                  lineHeight: 1.5,
                  maxWidth: 320,
                  flexShrink: 0
                }}>
                  셀프애드 팀이 확인 후, 프로젝트를 완료 처리합니다.<br />
                  완료 처리 시, 남은 계약금이 환불됩니다.
                </div>
              )}
            </div>
          </div>

          {/* 계약된 인플루언서 */}
          <div className="board">
            <div className="board__head" style={{ paddingBottom: 12 }}>
              <h3>계약된 인플루언서</h3>
              <span className="t-sub">
                {(currentProject.contractedInfluencers || []).length} / {currentProject.headcount || '-'}명
              </span>
            </div>

            {currentProject.contractedInfluencers && currentProject.contractedInfluencers.length > 0 ? (
              currentProject.contractedInfluencers.map((inf, idx) => {
                const statusInfo = getResultStatusLabel(inf.resultStatus);
                return (
                  <div className="icard" key={inf.applicationId || idx}>
                    <div className="icard__l">
                      <b>{inf.userName}</b>
                      <div className="icard__sns">
                        {inf.snsAccounts && inf.snsAccounts.length > 0 ? (
                          inf.snsAccounts.map((sns, snsIdx) => (
                            sns.url ? (
                              <a
                                key={snsIdx}
                                href={sns.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="platform"
                                style={{ textDecoration: 'none' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {getSNSIcon(sns.platform)}
                                @{sns.accountName || '-'}
                                {sns.followerCount > 0 && (
                                  <span style={{ color: '#6b7686' }}>
                                    · {formatFollowerCount(sns.followerCount)}
                                  </span>
                                )}
                              </a>
                            ) : (
                              <span key={snsIdx} className="platform">
                                {getSNSIcon(sns.platform)}
                                @{sns.accountName || '-'}
                                {sns.followerCount > 0 && (
                                  <span style={{ color: '#6b7686' }}>
                                    · {formatFollowerCount(sns.followerCount)}
                                  </span>
                                )}
                              </span>
                            )
                          ))
                        ) : (
                          <span className="t-sub">@{inf.userId || '-'}</span>
                        )}
                      </div>
                      <div className="icard__meta">
                        {inf.phoneNumber ? `${inf.phoneNumber} · ` : ''}
                        {inf.userAddress || '주소 정보 없음'}
                      </div>
                    </div>
                    <div className="icard__r">
                      <span>
                        계약일 {inf.contractAcceptedAt
                          ? new Date(inf.contractAcceptedAt).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })
                          : '-'}
                      </span>
                      <span className={`mb-badge ${getResultBadgeClass(inf.resultStatus)}`}>
                        결과물 {statusInfo.text}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ fontSize: 14, color: '#6b7686', textAlign: 'center', padding: '24px 0' }}>
                아직 계약된 인플루언서가 없습니다.
              </div>
            )}
          </div>
        </>
      )}

      {/* 프로젝트 상세 모달 */}
      {showDetailModal && currentProject && (
        <ProjectDetailModal
          open={showDetailModal}
          project={currentProject}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </div>
  );
}

export default MarketerProjectOnCheck;
